/**
 * useChatSystem — owns all freeform chat wiring for Variant C.
 *
 * Extracted from VariantCShell.tsx (Phase 4 refactor).
 *
 * Owns:
 *   - chatDocxPath state + effects (resolve on post-run, clear on idle)
 *   - useUserInputBridge (WebSocket approval bridge)
 *   - useResolvedRunIdentity (disk-backed identity fallback)
 *   - useSelectionCapture (captured selection for context pill)
 *   - useChatBridge (the core chat bridge)
 *   - buildCommandSnapshot builder
 *   - shouldEmitSuggestion predicate
 *   - emitSuggestionEvent dispatcher
 *   - handleSendChat adapter
 *   - useSelectionCitations
 */

import type { Dispatch } from "react";
import { useCallback, useEffect, useState } from "react";
import { adhocDraftSection, classifyChatIntent, getRunStatus } from "../../api/client";
import { useUserInputBridge } from "../../hooks/useUserInputBridge";
import { detectCursorSection, isValidLibraryId } from "../../services/wordWriter";
import type { Library, MentionAttachment } from "../../types";
import { type RouteContext, canTargetSection, decideRoute, withClassifierFallback } from "../chatIntentRouter";
import type { CommandSnapshot } from "../commandRegistry";
import { findCommandById } from "../commandRegistry";
import { type Suggestion, formatSuggestionText, suggestFromTextWithLLM } from "../commandSuggester";
import type { LoomFreshSelection } from "../components/LoomFresh";
import type { ResolvedScope } from "../cursorScope";
import type { EventLogEntry } from "../eventLog";
import type { RunIdentity } from "../sseAdapter";
import type { RunState, VariantCEvent } from "../state";
import { synthesizeProjectName } from "../utils/spineSnapshot";
import { type ChatDispatchTarget, buildStreamingStatus, useChatBridge } from "./useChatBridge";
import { useResolvedRunIdentity } from "./useResolvedRunIdentity";
import { type CapturedSelection, useSelectionCapture } from "./useSelectionCapture";
import { useSelectionCitations } from "./useSelectionCitations";
import type { SpineSection } from "./useVariantCRun";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Asthra's steady reply when the writer tries to chat with NO source library
 *  configured (typically a custom submission that skipped the library step).
 *  Asthra only reasons over trusted source data, so it declines every chat turn
 *  until a library is attached and points the writer at the two ways to do it. */
export const NO_LIBRARY_CHAT_REFUSAL =
  "Asthra only acts on trusted source data, so I can't help here yet. " +
  "Select one of your available libraries, or create a new library to add your sources, and I'll get to work.";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

export interface UseChatSystemParams {
  runState: RunState;
  runId: string | null;
  /** For shouldEmitSuggestion / emitSuggestionEvent dedup logic. */
  eventLogEntries: readonly EventLogEntry[];
  documentSessionId: string | null;
  activeSelection: LoomFreshSelection | null;
  selectedLibraryId: string | null;
  selectedDocCode: string | null;
  libraries: Library[];
  identity: RunIdentity | null;
  restoredConversationId: string | undefined;
  sections: SpineSection[];
  dispatch: Dispatch<VariantCEvent>;
  /** Optional shell hook: set section row to drafting immediately on chat regen submit. */
  onRegenSectionDetected?: (sectionId: string) => void;
  /** Optional shell hook: force run-stream reattach so spine catches live regen events. */
  onRegenReconnect?: () => void;
  /** LLM intent routing on chat submit — on unless the
   *  `chat_intent_routing_disabled` kill switch is set. */
  intentRoutingEnabled?: boolean;
  /** Shell hook: a chat-initiated regen/ad-hoc run started for this module —
   *  drives the live drafting-progress bubble in the chat thread. */
  onChatRunActivity?: (
    activity: {
      moduleId: string;
      kind: "regen" | "adhoc";
      instruction: string;
    } | null,
  ) => void;
  /** Shell hook: the writer asked to edit the highlighted text — hand off
   *  to the highlight-edit flow (proposal card with Approve/Reject). */
  onHighlightEditRequested?: (req: {
    selectedText: string;
    instruction: string;
    runId: string | null;
    sectionId: string | null;
    sectionNumber: string | null;
    /** Writer-attached source assets (@-mentions) riding on the message. */
    attachments?: MentionAttachment[];
  }) => void;
  /** Shell hook: the writer asked to revise the cursor section's existing
   *  content — hand off to the edit-draft request (result lands as a
   *  mode:"edit" Approve/Reject proposal card). */
  onSectionEditRequested?: (req: {
    runId: string;
    sectionId: string;
    sectionNumber: string | null;
    instruction: string;
    /** Writer-attached source assets (@-mentions) riding on the message. */
    attachments?: MentionAttachment[];
  }) => void;
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

export interface UseChatSystemResult {
  chat: ReturnType<typeof useChatBridge>;
  handleSendChat: (
    text: string,
    scope: ResolvedScope,
    quotedMessage?: import("../messageProjection").QuotedMessage,
    attachments?: MentionAttachment[],
  ) => Promise<string | null>;
  /** Stop the in-flight chat run — aborts the fetch, denies any pending
   *  approval, and posts a "you stopped the run" notice. Wired to the
   *  composer's Stop button. */
  handleStopChat: () => void;
  buildCommandSnapshot: (overrides?: Partial<CommandSnapshot>) => CommandSnapshot;
  emitSuggestionEvent: (suggestion: Suggestion | null) => void;
  selectionCitations: ReturnType<typeof useSelectionCitations>;
  captureSelection: () => Promise<void>;
  capturedSelection: CapturedSelection | null;
  dismissSelection: () => void;
  /** Forward the selection-chip probe's text into the captured selection. */
  syncSelectionFromProbe: (text: string | null) => void;
  pendingApproval: ReturnType<typeof useUserInputBridge>["pendingApproval"];
  submitApproval: ReturnType<typeof useUserInputBridge>["submitApproval"];
  rejectApproval: ReturnType<typeof useUserInputBridge>["rejectApproval"];
  bridgeStatus: ReturnType<typeof useUserInputBridge>["status"];
  buildStreamingStatus: typeof buildStreamingStatus;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useChatSystem(params: UseChatSystemParams): UseChatSystemResult {
  const {
    runState,
    runId,
    eventLogEntries,
    documentSessionId,
    activeSelection,
    selectedLibraryId,
    selectedDocCode,
    libraries,
    identity,
    restoredConversationId,
    sections,
    dispatch,
    onRegenSectionDetected,
    onRegenReconnect,
    intentRoutingEnabled,
    onChatRunActivity,
    onHighlightEditRequested,
    onSectionEditRequested,
  } = params;

  // -- chatDocxPath -------------------------------------------------------
  // Resolved on post-run by fetching /api/runs/{runId}. Immutable for
  // a given run so we only fetch once and skip refetches.
  const [chatDocxPath, setChatDocxPath] = useState<string | null>(null);

  useEffect(() => {
    if (runState !== "post-run" || !runId || chatDocxPath) return;
    let cancelled = false;
    getRunStatus(runId)
      .then((status) => {
        if (cancelled) return;
        if (status.docx_path) setChatDocxPath(status.docx_path);
      })
      .catch((err) => {
        if (typeof console !== "undefined") {
          console.info("[useChatSystem] chat docx_path resolution failed", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [runState, runId, chatDocxPath]);

  // Reset path when the writer explicitly starts over.
  useEffect(() => {
    if (runState === "idle") setChatDocxPath(null);
  }, [runState]);

  // -- WebSocket bridge ---------------------------------------------------
  const userInputBridge = useUserInputBridge(documentSessionId || undefined);

  // -- Disk-backed identity fallback ------------------------------------
  const resolvedFallback = useResolvedRunIdentity(activeSelection ? synthesizeProjectName(activeSelection) : null);

  // -- Selection capture --------------------------------------------------
  const {
    captured: capturedSelection,
    captureNow: captureSelection,
    dismiss: dismissSelection,
    syncFromProbe: syncSelectionFromProbe,
  } = useSelectionCapture();

  // -- Chat bridge -------------------------------------------------------
  const chat = useChatBridge({
    docxPath: chatDocxPath,
    trial: identity?.trial ?? resolvedFallback.trial ?? undefined,
    session: identity?.session ?? resolvedFallback.session ?? undefined,
    docType: activeSelection?.backendDocType,
    libraryId: activeSelection?.libraryId ?? (selectedLibraryId || undefined),
    runId,
    connectionId: userInputBridge.connectionId,
    bridgeStatus: userInputBridge.status,
    initialConversationId: restoredConversationId,
    useAgentV3: false,
    onRegenSubmitted: useCallback(
      (moduleId: string | null) => {
        const byId = moduleId ? sections.find((s) => s.id === moduleId) : null;
        const fallback = !byId && sections.length === 1 ? sections[0] : null;
        const target = byId ?? fallback;
        if (!target) return;
        onRegenSectionDetected?.(target.id);
        onRegenReconnect?.();
        dispatch({
          type: "SECTION_ACTIVATED",
          section: {
            sectionId: target.id,
            sectionNumber: target.number,
            title: target.title,
          },
        });
      },
      [sections, dispatch, onRegenSectionDetected, onRegenReconnect],
    ),
    onEvent: useCallback(
      (entry) => {
        dispatch({ type: "EVENT_APPENDED", entry });
      },
      [dispatch],
    ),
  });

  // -- Stop the chat run --------------------------------------------------
  // Wired to the composer's Stop button. Three concerns, in order:
  //   1. abortChat() drops the /chat-stream fetch → the backend SSE handler
  //      sees the disconnect and cancels the tool loop (no more rounds /
  //      approval prompts).
  //   2. rejectApproval() denies any approval prompt already on screen
  //      (no-op when none is pending) so it disappears immediately.
  //   3. emit an ``agent_note`` bubble intimating the writer stopped it.
  const handleStopChat = useCallback(() => {
    chat.abortChat();
    userInputBridge.rejectApproval();
    dispatch({
      type: "EVENT_APPENDED",
      entry: {
        ts: Date.now(),
        actor: "asthra",
        sectionId: null,
        runId: runId ?? null,
        payload: { kind: "agent_note", text: "You stopped the run." },
      },
    });
  }, [chat, userInputBridge, dispatch, runId]);

  // -- buildCommandSnapshot ----------------------------------------------
  const buildCommandSnapshot = useCallback(
    (overrides: Partial<CommandSnapshot> = {}): CommandSnapshot => ({
      hasDoctype: !!selectedDocCode,
      hasLibrary: !!selectedLibraryId,
      hasAnyLibrary: libraries.length > 0,
      runState,
      ...overrides,
    }),
    [selectedDocCode, selectedLibraryId, libraries.length, runState],
  );

  // -- shouldEmitSuggestion ----------------------------------------------
  const shouldEmitSuggestion = useCallback(
    (suggestion: Suggestion | null): boolean => {
      if (!suggestion) return false;
      if (!findCommandById(suggestion.commandId)) return false;
      for (let i = eventLogEntries.length - 1; i >= 0; i -= 1) {
        const e = eventLogEntries[i];
        if (e.payload.kind === "agent_note") {
          if (e.payload.commandId === suggestion.commandId) return false;
          break;
        }
      }
      return true;
    },
    [eventLogEntries],
  );

  // -- emitSuggestionEvent -----------------------------------------------
  // ``pairedPromptTs`` — when this suggestion is the reply to a writer
  // prompt dispatched in the same synchronous frame, the caller passes
  // the prompt's ``ts`` so the agent_note lands at ``promptTs + 1``.
  // Two Date.now() calls in immediate succession routinely return the
  // same millisecond; without this offset the user prompt + suggestion
  // reply collide on ``at`` and ``mergeMessages`` drops one of them
  // (matching the same ``ts + 1`` pattern messageProjection.ts uses for
  // within-entry paired messages — see askwhy / library / doctype).
  const emitSuggestionEvent = useCallback(
    (suggestion: Suggestion | null, pairedPromptTs?: number) => {
      if (!suggestion) return;
      const command = findCommandById(suggestion.commandId);
      if (!command) return;
      for (let i = eventLogEntries.length - 1; i >= 0; i -= 1) {
        const e = eventLogEntries[i];
        if (e.payload.kind === "agent_note") {
          if (e.payload.commandId === suggestion.commandId) return;
          break;
        }
      }
      const ts = pairedPromptTs !== undefined ? pairedPromptTs + 1 : Date.now();
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts,
          actor: "asthra",
          sectionId: null,
          runId,
          payload: {
            kind: "agent_note",
            text: formatSuggestionText(suggestion, command),
            commandId: command.id,
            source: suggestion.source,
          },
        },
      });
    },
    [eventLogEntries, runId, dispatch],
  );

  // -- handleSendChat ----------------------------------------------------
  const handleSendChat = useCallback(
    async (
      text: string,
      scope: ResolvedScope,
      quotedMessage?: import("../messageProjection").QuotedMessage,
      attachments?: MentionAttachment[],
    ): Promise<string | null> => {
      // No source library → Asthra does no chat work. It only acts on trusted
      // source data, so any session without a library selected (in practice a
      // custom submission that skipped the library step) can't be reasoned
      // over. Echo the writer's prompt so the thread reflects what they asked,
      // then reply with a steady refusal pointing them at the two ways to add
      // sources. Runs before the suggestion + intent-routing paths so nothing
      // downstream (which all assume a library) fires.
      const hasSourceLibrary = isValidLibraryId(activeSelection?.libraryId) || isValidLibraryId(selectedLibraryId);
      if (!hasSourceLibrary) {
        const promptTs = Date.now();
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: promptTs,
            actor: "you",
            sectionId: null,
            runId,
            payload: { kind: "prompt", text, attachments },
          },
        });
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: promptTs + 1,
            actor: "asthra",
            sectionId: null,
            runId,
            payload: { kind: "agent_note", text: NO_LIBRARY_CHAT_REFUSAL },
          },
        });
        return null;
      }

      const suggestion = await suggestFromTextWithLLM(text, buildCommandSnapshot());
      if (suggestion && shouldEmitSuggestion(suggestion)) {
        // Capture a single ts and stamp the agent_note reply at
        // ``promptTs + 1`` so the pair can't collide in mergeMessages
        // (keyed on at + role; the offset survives the role-keyed
        // fix too — it's defence in depth and keeps the bubbles
        // chronologically stable).
        const promptTs = Date.now();
        dispatch({
          type: "EVENT_APPENDED",
          entry: {
            ts: promptTs,
            actor: "you",
            sectionId: null,
            runId,
            payload: { kind: "prompt", text, attachments },
          },
        });
        emitSuggestionEvent(suggestion, promptTs);
        return null;
      }
      let enrichedScope = scope;
      let cursorSectionNumber: string | undefined;
      if (scope.kind === "document") {
        const cursorSection = await detectCursorSection();
        if (cursorSection?.sectionNumber) {
          cursorSectionNumber = cursorSection.sectionNumber;
          enrichedScope = {
            kind: "cursor",
            sectionNumber: cursorSection.sectionNumber,
            label: `§${cursorSection.sectionNumber}`,
            hint: `Cursor is in §${cursorSection.sectionNumber}.`,
          };
        }
      }
      const sectionNumber =
        enrichedScope.kind === "inspector" || enrichedScope.kind === "cursor"
          ? enrichedScope.sectionNumber
          : cursorSectionNumber;
      const sectionId = sectionNumber ? (sections.find((s) => s.number === sectionNumber)?.id ?? null) : null;
      const selectedText = quotedMessage ? undefined : capturedSelection?.text || undefined;
      const target: ChatDispatchTarget = {
        sectionNumber,
        sectionId,
        selectedText,
        quotedMessage,
        attachments,
      };

      // -- Intent routing (kill switch: chat_intent_routing_disabled) ----
      // Fast Haiku classify, then route to a purpose-built flow. Every
      // failure path lands in plain chat — the classifier client never
      // throws, and decideRoute re-validates the context client-side.
      if (intentRoutingEnabled) {
        const sectionStatus = sectionNumber ? (sections.find((s) => s.number === sectionNumber)?.status ?? null) : null;
        const routeCtx: RouteContext = {
          runId,
          runState,
          sectionId,
          sectionNumber: sectionNumber ?? null,
          selectedText: selectedText ?? null,
          sectionStatus,
        };
        const intentResp = await classifyChatIntent({
          message: text,
          has_highlight: Boolean(selectedText),
          highlight_preview: selectedText ? selectedText.slice(0, 500) : undefined,
          cursor_section_number: sectionNumber ?? undefined,
          can_target_section: canTargetSection(routeCtx),
          doc_type: activeSelection?.backendDocType || undefined,
        });
        // If the classifier never answered (client timeout / transport
        // error) a highlight + imperative rewrite still routes to the
        // edit flow — same heuristic the server applies before its LLM.
        const action = decideRoute(withClassifierFallback(intentResp, routeCtx, text), routeCtx, text);
        // The intent that will ACTUALLY run (decideRoute may downgrade
        // the server's pick to plain chat when the client context no
        // longer supports it) — stamped on the prompt EventLog entry so
        // the chip in the thread reflects the executed action, and
        // carried on the dispatch target so the streaming status line
        // keeps the label for the chat route.
        const classifiedIntent: import("../../api/client").ChatRoutedIntent =
          action.kind === "chat" ? (action.mode === "research" ? "research_highlight" : "chat") : action.kind;
        target.intent = classifiedIntent;

        // Routed (non-chat) actions emit the writer's prompt entry here —
        // chat.sendChat won't run, so nothing else records it.
        const emitPromptEntry = () => {
          dispatch({
            type: "EVENT_APPENDED",
            entry: {
              ts: Date.now(),
              actor: "you",
              sectionId,
              runId,
              payload: {
                kind: "prompt",
                text,
                selectionContext: selectedText || undefined,
                intent: classifiedIntent,
                attachments,
              },
            },
          });
        };

        if (action.kind === "edit_section" && onSectionEditRequested) {
          // Chat "edit section" (2026-07-21): revise the cursor
          // section's EXISTING content per the writer's comments (+
          // tagged attachments; library lookup only if the model needs
          // it). The shell owns the request lifecycle — mirror of the
          // edit_highlight callback below. Full redrafts are NOT a chat
          // intent anymore; they run via the /regen command.
          emitPromptEntry();
          onSectionEditRequested({
            runId: action.runId,
            sectionId: action.sectionId,
            sectionNumber: action.sectionNumber,
            instruction: action.instruction,
            attachments,
          });
          dismissSelection();
          return null;
        }

        if (action.kind === "draft_content") {
          emitPromptEntry();
          try {
            onRegenReconnect?.();
            await adhocDraftSection(action.runId, action.sectionId, {
              instruction: action.instruction,
              attachments,
            });
            onChatRunActivity?.({
              moduleId: action.sectionId,
              kind: "adhoc",
              instruction: text,
            });
            dismissSelection();
            return null;
          } catch (err) {
            const detail = err instanceof Error ? err.message : String(err);
            return `Couldn't start the ad-hoc draft: ${detail}`;
          }
        }

        if (action.kind === "edit_highlight" && onHighlightEditRequested) {
          emitPromptEntry();
          onHighlightEditRequested({
            selectedText: action.selectedText,
            instruction: action.instruction,
            runId: action.runId,
            sectionId: action.sectionId,
            sectionNumber: action.sectionNumber,
            attachments,
          });
          dismissSelection();
          return null;
        }

        if (action.kind === "chat" && action.mode === "research") {
          target.mode = "research";
        }
      }

      const result = await chat.sendChat(text, enrichedScope, target);
      dismissSelection();
      return result;
    },
    [
      chat,
      capturedSelection,
      dismissSelection,
      sections,
      buildCommandSnapshot,
      shouldEmitSuggestion,
      emitSuggestionEvent,
      runId,
      runState,
      dispatch,
      intentRoutingEnabled,
      activeSelection,
      selectedLibraryId,
      onRegenSectionDetected,
      onRegenReconnect,
      onChatRunActivity,
      onHighlightEditRequested,
      onSectionEditRequested,
    ],
  );

  // -- Selection citations -----------------------------------------------
  const selectionCitations = useSelectionCitations({
    trial: identity?.trial ?? resolvedFallback.trial ?? undefined,
    session: identity?.session ?? resolvedFallback.session ?? undefined,
    docType: activeSelection?.backendDocType ?? "",
  });

  return {
    chat,
    handleSendChat,
    handleStopChat,
    buildCommandSnapshot,
    emitSuggestionEvent,
    selectionCitations,
    captureSelection,
    capturedSelection,
    dismissSelection,
    syncSelectionFromProbe,
    pendingApproval: userInputBridge.pendingApproval,
    submitApproval: userInputBridge.submitApproval,
    rejectApproval: userInputBridge.rejectApproval,
    bridgeStatus: userInputBridge.status,
    buildStreamingStatus,
  };
}
